import { DocumentsTable } from 'components';
import { useFormContext } from 'libs/form/useFormContext';
import { useTranslation } from 'libs/i18n';
import { IconButton, Typography } from 'libs/ui';
import { Icon } from 'libs/ui/Icon';
import {
  DialogDeleteAndReasonChange,
  DialogReasonChange,
  Section,
} from 'modules/client/common/components';
import { UnidentifiableBeneficiaryMessageWrapperStyled } from 'modules/client/legal/components/sections/CompanyBeneficiariesSection/CompanyBeneficiariesSection.styles';

import { BeneficiariesInfoCard } from './BeneficiariesInfoCard';
import { CompanyBeneficiariesSectionProps } from './types';
import { useCompanyBeneficiariesSection } from './useCompanyBeneficiariesSection';

export const CompanyBeneficiariesSection = (
  props: CompanyBeneficiariesSectionProps
) => {
  const { t } = useTranslation();

  const { readOnly } = useFormContext();

  const {
    hasBeneficiaries,
    dialogLoading,
    isOpenArchiveDialog,
    isOpenDeleteDialog,
    beneficiariesItems,
    hasUnidentifiableBeneficiaryLetterFiles,
    shouldShowUnidentifiableBeneficiaryContent,
    unidentifiableBeneficiaryLetterFiles,
    shouldShowAddButton,
    onClickArchive,
    onClickDelete,
    onClickBeneficiary,
    onClickAddBeneficiary,
    onCloseArchiveDialog,
    onCloseDeleteDialog,
    onArchiveAndDearchive,
    onDelete,
  } = useCompanyBeneficiariesSection(props);

  return (
    <Section
      datax="companyBeneficiariesSection"
      headerRight={
        shouldShowAddButton && (
          <IconButton
            icon={<Icon.Plus size={16} />}
            mode="outlined"
            sizeVariant="xs"
            onClick={onClickAddBeneficiary}
          />
        )
      }
      title={t('client.legal.companyBeneficiaries.title')}
    >
      {shouldShowUnidentifiableBeneficiaryContent && (
        <UnidentifiableBeneficiaryMessageWrapperStyled>
          <Typography variant="captionMedium">
            {t(
              'client.legal.companyBeneficiaries.unidentifiableBeneficiaryMessage'
            )}
          </Typography>
        </UnidentifiableBeneficiaryMessageWrapperStyled>
      )}
      {hasBeneficiaries && (
        <BeneficiariesInfoCard
          editable={!readOnly}
          items={beneficiariesItems}
          onCellClick={(item) => onClickBeneficiary(item.id)}
          onClickArchive={onClickArchive}
          onClickDelete={onClickDelete}
        />
      )}
      {hasUnidentifiableBeneficiaryLetterFiles && (
        <>
          <Typography variant="subtitle">
            {t('client.legal.companyBeneficiaries.documentTitle')}
          </Typography>
          <DocumentsTable files={unidentifiableBeneficiaryLetterFiles ?? []} />
        </>
      )}

      {/** Delete dialog */}
      <DialogDeleteAndReasonChange
        deleteLoading={dialogLoading}
        isOpen={isOpenDeleteDialog}
        onClose={onCloseDeleteDialog}
        onDelete={onDelete}
      />

      {/** Archive dialog */}
      <DialogReasonChange
        isOpen={isOpenArchiveDialog}
        submitLoading={dialogLoading}
        onClose={onCloseArchiveDialog}
        onSave={onArchiveAndDearchive}
      />
    </Section>
  );
};
